import { useSignal } from "@preact/signals";
import { useRef } from "preact/hooks";
import {
  DEFAULT_OPTIONS,
  DUCK_DB,
  MUSIC_DB,
  PACES,
  type Pace,
  type ProjectOptions,
  type Pronunciation,
  VOICES,
  type VoiceSetting,
} from "~/protocol";
import { projectBase } from "~/web/src/api";
import type { Channel } from "~/web/src/channel";
import { errorText } from "~/web/src/errors";
import { SheetHead } from "~/web/src/project/sheet-head";

// The audio sheet (mockup ux/06, spec: docs/project/audio-spec.md): voice,
// pace, music (direction + Replace track, level), pronunciations. Edits live
// in a draft over the project's options until Save and re-mix writes them in
// one update_project and tells the director in one chat line — it works out
// what to redo (a re-voice when the speech settings moved, a re-render
// otherwise). Replace track is immediate, like the old Generate: the music
// lands as a file, not a setting. ▶ on a voice plays our committed sample; ▶
// on a pronunciation asks the server to say the replacement in the project
// voice at its pace.

const SAMPLE_TEXT = "create amazing videos with featurette";
const LABEL: Record<keyof ProjectOptions, string> = {
  voice: "voice",
  pace: "pace",
  musicDb: "music level",
  pronunciations: "pronunciations",
  musicPrompt: "music direction",
  interactive: "mode", // never edited here; keeps the record total
};

const complete = (list: readonly Pronunciation[]) =>
  list
    .map((p) => ({ word: p.word.trim(), say: p.say.trim() }))
    .filter((p) => p.word !== "" && p.say !== "");
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

export function AudioSheet({ channel, onClose }: { channel: Channel; onClose: () => void }) {
  const project = channel.project.value;
  // null = no edits, the controls follow the project (the script editor's regime).
  const draft = useSignal<Partial<ProjectOptions> | null>(null);
  const promptDraft = useSignal<string | null>(null);
  const notice = useSignal<string | null>(null);
  const busy = useSignal(false);
  const previewing = useSignal<string | null>(null);
  const player = useRef<HTMLAudioElement>(null);
  if (!project) return null;

  const options = project.options;
  const view: ProjectOptions = { ...options, ...draft.value };
  const rows = view.pronunciations;
  const changed = (Object.keys(DEFAULT_OPTIONS) as (keyof ProjectOptions)[]).filter((k) =>
    k === "pronunciations"
      ? !same(complete(rows), options.pronunciations)
      : k !== "musicPrompt" && !same(view[k], options[k]),
  );
  const dirty = changed.length > 0;
  const idle = channel.agent.value.status === "idle" && channel.connected.value;
  const canSave = dirty && idle && !busy.value;
  const generating = channel.files.value.some((f) => f.pending);
  const silent = view.voice === "none";

  function set(patch: Partial<ProjectOptions>) {
    draft.value = { ...draft.value, ...patch };
    notice.value = null;
  }
  function setRow(i: number, patch: Partial<Pronunciation>) {
    set({ pronunciations: rows.map((p, j) => (j === i ? { ...p, ...patch } : p)) });
  }

  async function save() {
    busy.value = true;
    try {
      const patch: Partial<ProjectOptions> = {};
      for (const k of changed) {
        if (k === "pronunciations") patch.pronunciations = complete(rows);
        else if (k === "voice") patch.voice = view.voice;
        else if (k === "pace") patch.pace = view.pace;
        else if (k === "musicDb") patch.musicDb = view.musicDb;
      }
      const r = await channel.send({ kind: "update_project", options: patch });
      if (!r.ok) {
        notice.value =
          r.error === "bad_option"
            ? "a setting was refused — check the values"
            : `save failed: ${r.error}`;
        return;
      }
      draft.value = null;
      const speech = changed.filter((k) => k !== "musicDb").map((k) => LABEL[k]);
      const text =
        speech.length > 0
          ? `I changed the audio settings (${changed.map((k) => LABEL[k]).join(", ")}). ` +
            "Re-create the voiceover and re-render the video."
          : "I changed the music level. Re-render the video.";
      const c = await channel.send({ kind: "chat", text });
      if (c.ok) onClose();
      else notice.value = "saved — the director is busy, tell it when it is done";
    } finally {
      busy.value = false;
    }
  }

  function replaceTrack() {
    const prompt = (promptDraft.value ?? options.musicPrompt).trim();
    if (!prompt) return;
    promptDraft.value = null;
    if (prompt !== options.musicPrompt) {
      void channel.send({ kind: "update_project", options: { musicPrompt: prompt } });
    }
    void channel.send({ kind: "generate_music", prompt });
  }

  function play(src: string) {
    const el = player.current;
    if (!el) return;
    if (el.src.startsWith("blob:")) URL.revokeObjectURL(el.src);
    el.src = src;
    void el.play().catch(() => {});
  }
  async function preview(say: string) {
    previewing.value = say;
    try {
      const res = await fetch(`${projectBase(channel.id)}/say?text=${encodeURIComponent(say)}`);
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `${res.status} ${res.statusText}`);
      }
      play(URL.createObjectURL(await res.blob()));
    } catch (err) {
      notice.value = `preview failed: ${errorText(err)}`;
    } finally {
      previewing.value = null;
    }
  }

  const paceIndex = Math.max(0, PACES.indexOf(view.pace));
  const underVoice = Math.round(view.musicDb + DUCK_DB);

  return (
    <>
      <SheetHead title="Audio" onClose={onClose} />
      <div class="sb">
        <div class="opt">
          <div class="lab">Voice</div>
          <div class="voice-grid">
            {[
              ...VOICES,
              { id: "none" as const, name: "No voiceover", blurb: "captions and music only" },
            ].map((v) => (
              <label key={v.id} class={`voice-card ${view.voice === v.id ? "on" : ""}`}>
                <input
                  type="radio"
                  name="voice"
                  class="sr"
                  checked={view.voice === v.id}
                  onChange={() => set({ voice: v.id as VoiceSetting })}
                />
                <span class="who">
                  <b>{v.name}</b>
                  <span>{v.blurb}</span>
                </span>
                {v.id !== "none" && (
                  <button
                    type="button"
                    class="btn ghost sm"
                    aria-label={`Play a sample of ${v.name}`}
                    title={`“${SAMPLE_TEXT}”`}
                    onClick={() => play(`/samples/${v.id}.mp3`)}
                  >
                    ▶
                  </button>
                )}
              </label>
            ))}
          </div>
        </div>

        <div class="opt">
          <div class="lab">Pace</div>
          <div class="slider">
            <span>slower</span>
            <input
              type="range"
              aria-label="Pace"
              min={0}
              max={PACES.length - 1}
              step={1}
              value={paceIndex}
              disabled={silent}
              title={`×${view.pace}`}
              onInput={(e) => set({ pace: PACES[Number(e.currentTarget.value)] as Pace })}
            />
            <span>faster</span>
          </div>
        </div>

        <div class="opt">
          <div class="lab">Music</div>
          <textarea
            class="audio-prompt"
            aria-label="Music direction"
            value={promptDraft.value ?? options.musicPrompt}
            onInput={(e) => {
              promptDraft.value = e.currentTarget.value;
            }}
          />
          <div class="row">
            <span class="dim">
              The direction a new track is generated from; the length goes in the text.
            </span>
            <button type="button" class="btn ghost" disabled={generating} onClick={replaceTrack}>
              {generating ? "generating…" : "Replace track"}
            </button>
          </div>
          <div class="slider">
            <span>volume</span>
            <input
              type="range"
              aria-label="Music level"
              min={MUSIC_DB.min}
              max={MUSIC_DB.max}
              step={MUSIC_DB.step}
              value={view.musicDb}
              onInput={(e) => set({ musicDb: Number(e.currentTarget.value) })}
            />
            <span>
              {view.musicDb} dB{silent ? "" : ` · ${underVoice} dB under voice`}
            </span>
          </div>
        </div>

        <div class="opt">
          <div class="lab">Pronunciation</div>
          {rows.map((p, i) => (
            <div class="pron" key={i}>
              <input
                aria-label="Word"
                placeholder="Word"
                value={p.word}
                onInput={(e) => setRow(i, { word: e.currentTarget.value })}
              />
              <span class="dim">→</span>
              <input
                aria-label="Said as"
                placeholder="par-KAY or /pɑːrˈkeɪ/"
                value={p.say}
                onInput={(e) => setRow(i, { say: e.currentTarget.value })}
              />
              <button
                type="button"
                class="btn ghost sm"
                aria-label="Hear it"
                disabled={silent || p.say.trim() === "" || previewing.value !== null}
                onClick={() => void preview(p.say.trim())}
              >
                {previewing.value === p.say.trim() ? "…" : "▶"}
              </button>
              <button
                type="button"
                class="btn ghost sm"
                aria-label="Remove"
                onClick={() => set({ pronunciations: rows.filter((_, j) => j !== i) })}
              >
                ✕
              </button>
            </div>
          ))}
          <button
            type="button"
            class="btn ghost"
            onClick={() => set({ pronunciations: [...rows, { word: "", say: "" }] })}
          >
            Add a word
          </button>
        </div>
        {/* biome-ignore lint/a11y/useMediaCaption: voice samples and one-word previews */}
        <audio ref={player} preload="none" />
      </div>
      <div class="sf">
        <span class={`note ${notice.value ? "notice" : ""}`}>
          {notice.value ?? "Voice and music re-mix without re-filming."}
        </span>
        <button type="button" class="btn ghost" onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          class="btn"
          disabled={!canSave}
          title={dirty && !idle ? "Wait for the director to finish" : undefined}
          onClick={() => void save()}
        >
          Save and re-mix
        </button>
      </div>
    </>
  );
}
